import { useQuery } from '@apollo/client';
import { AddCircle, VerifiedUser } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { RolesOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { RoleListDocument } from './RoleList.generated';
import { NewRoleDialog } from './components/NewRoleDialog';
import { RoleTable } from './components/RoleTable';

export const RoleList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { orderBy } = useOrderByParams<RolesOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading, error } = useQuery(RoleListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.roles.pageInfo),
  });

  const [newRoleOpen, setNewRoleOpen] = useState(false);

  const roles = useMemo(() => data?.network?.roles.nodes ?? [], [data]);
  const totalCount = useTotalCount(data?.network?.roles.pageInfo.nodesCount);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <>
      <Toolbar
        actions={
          <Button
            color="primary"
            onClick={() => setNewRoleOpen(true)}
            startIcon={<AddCircle />}
            variant="contained"
          >
            {t('pages.roles.buttons.new_role')}
          </Button>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<VerifiedUser />}
        titleText={t('pages.roles.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.roles.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count selectedCount={0} totalCount={totalCount} thing="pages.roles.thing_plural" />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <RoleTable loading={loading} rows={roles} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
      {newRoleOpen && <NewRoleDialog fullWidth onClose={() => setNewRoleOpen(false)} open />}
    </>
  );
};
